import { INavMenuLabels, useCurrentUserStore, TNavbarPage } from '@diana/core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES_NS } from '../i18n/routes';

export type TNavMenu = 'work' | 'performance' | 'people' | 'assets' | 'home';
export const AvailableNavMenus: TNavMenu[] = ['work', 'performance', 'people', 'assets', 'home'];

export const useNavMenuItems = () => {
  const uiLabels = useUINavMenuLabels();
  const menu = useCurrentUserStore((ctx) => ctx.info?.environmentSettings?.menu);

  const navLabels = useMemo(() => {
    return AvailableNavMenus.reduce((acc, navMenu) => {
      const item = menu?.find((item) => item.feature === navMenu);
      if (item) acc[navMenu] = item.label;
      return acc;
    }, {} as Record<TNavMenu, string>);
  }, [menu]);

  const getNavMenuLabel = useCallback(
    (navMenu: TNavMenu) => {
      return navLabels[navMenu] ?? uiLabels[navMenu as keyof typeof uiLabels];
    },
    [navLabels, uiLabels],
  );

  const getNavMenuTarget = useCallback(
    (navMenu: TNavMenu) => {
      const item = menu?.find((item) => item.feature === navMenu);
      if (item) return item.target;
      return '_self';
    },
    [menu],
  );

  const isNavMenuHidden = useCallback(
    (navMenu: TNavMenu) => {
      const item = menu?.find((item) => item.feature === navMenu);
      if (item) return !item.show;
      return !navLabels[navMenu];
    },
    [menu, navLabels],
  );

  const getExternalLinks = useCallback(() => {
    const filteredMenu = menu?.filter((item) => item.external) || [];
    return filteredMenu.map(
      (item) =>
        ({
          path: normalizeUrl(item.link),
          label: item.label,
          hide: !item.show,
          target: item.target,
          pageName: item.feature as any,
          dataTestId: `${item.feature}-external-nav`,
        } as TNavbarPage),
    );
  }, [menu]);

  function normalizeUrl(url: string): string {
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  }

  return {
    getNavMenuLabel,
    getNavMenuTarget,
    isNavMenuHidden,
    getExternalLinks,
  };
};

export function useUINavMenuLabels(): INavMenuLabels {
  const { t } = useTranslation(ROUTES_NS);
  return useMemo(
    () => ({
      home: t('home'),
      work: t('work'),
      performance: t('performance'),
      people: t('people'),
      assets: t('assets'),
    }),
    [t],
  );
}
